import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import { getListTransactions, getListUser } from '../../services/transaction.service';
import { getLocations } from '../../services/inventoryCorrections.service';
import { useResponsive } from '../../hook/useResponsive';
import ViewIcon from '../../images/ViewIcon';
import {
  ListColumn,
  ListFilter,
  ListFilterOption,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  cells,
  formatUpdatedAt,
  t,
} from '../../components/list';

interface TransactionRow {
  id: string;
  invoiceNo?: string;
  txnDate?: string;
  amount?: number;
  tableName?: string | null;
  user?: { firstName?: string; lastName?: string } | null;
  location?: { name?: string } | null;
}

const userNameOf = (row: TransactionRow) =>
  ((row?.user?.firstName ?? '') + ' ' + (row?.user?.lastName ?? '')).trim();

const RecentSales = () => {
  const preLanguage = localStorage.getItem('language');
  const [language, setLanguage] = useState<any>(null);
  const { isMobile } = useResponsive();
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const inforTable: any = {
    title:
      language && language?.['No Recent Sales Found']
        ? language?.['No Recent Sales Found']
        : 'No Recent Sales Found',
    path: '/recentSales/',
    desc:
      language && language?.['A short description about this.']
        ? language?.['A short description about this.']
        : 'A short description about this.',
  };
  const companyId = localStorage.getItem('companyId');
  const locationId = localStorage.getItem('locationId');
  const userId = localStorage.getItem('userId');
  const [totalSales, setTotalSales] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  /**
   * One page of transactions. The search box maps onto `searchNo`, the
   * filters onto the endpoint's per-field `searchUser` / `searchLocation` /
   * `searchAmount` / `searchTable` params, and the only sort the endpoint knows is the
   * direction over createdAt (`sort`).
   */
  const fetchTransactions = async (query: ListQuery) => {
    webLoadingFalse();
    const response: any = await getListTransactions(
      String(query.page),
      query.limit,
      query.filters.searchAmount ?? '',
      query.filters.searchLocation ?? '',
      query.search,
      query.filters.searchUser ?? '',
      query.sortDir,
      locationId || '',
      companyId || '',
      query.filters.searchTable ?? ''
    );
    setTotalSales(response.data.data.totalSales);
    return {
      rows: response.data.data.transactions ?? [],
      count: response.data.data.count ?? 0,
    };
  };

  const getUsers = async () => {
    try {
      const response: any = await getListUser(companyId, 1, 100);
      setUsers(response.data.data ?? []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const getListContry = async () => {
    try {
      const response: any = await getLocations(userId, companyId, 0, 0, '');
      setLocations(response.data.data ?? []);
    } catch (error) {
      console.error('Error loading terms:', error);
    }
  };

  useEffect(() => {
    getListContry();
    getUsers();
    const handleNewToken = () => {
      getListContry();
      getUsers();
    };
    document.addEventListener('newToken', handleNewToken);
    return () => {
      document.removeEventListener('newToken', handleNewToken);
    };
  }, []);

  /** The desktop Date cell's wording ("Today 09:20", "18 Aug 08:50"); older years keep their time too. */
  const dateTimeOf = (value: any): string => {
    const label = formatUpdatedAt(value, language);
    if (!label || /\d{2}:\d{2}$/.test(label)) return label;
    const d = new Date(value);
    return `${label} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  /** "Table 12" on the mobile card for a bare number; named tables ("Terrace 4") read as they are. */
  const tableLabelOf = (name?: string | null): string => {
    const value = (name ?? '').trim();
    return /^\d/.test(value) ? `${t(language, 'Table', 'Table')} ${value}` : value;
  };

  const goToSale = (id: string) => webNavigate('/' + preLanguage + '/recentSales/' + id);

  /* ---------------- List kit configuration ---------------- */

  const columns: ListColumn<TransactionRow>[] = [
    {
      key: 'invoiceNo',
      label: 'Sales No.',
      width: 180,
      hideable: false,
      render: cells.primary(s => s?.invoiceNo ?? ' - '),
      value: s => s?.invoiceNo ?? ' - ',
      mobile: 'title',
      onClick: s => goToSale(String(s.id)),
    },
    {
      key: 'txnDate',
      label: 'Date',
      width: 150,
      // Fits "Yesterday 21:47" at the narrowest desktop width (1024px with the host sidebar).
      minWidth: 112,
      // The endpoint orders by createdAt only; the direction is the `sort` param.
      sortable: true,
      sortKey: 'createdAt',
      render: cells.date(s => s?.txnDate),
      value: s => dateTimeOf(s?.txnDate),
      mobile: 'subtitle',
    },
    {
      key: 'tableName',
      label: 'Table',
      width: 90,
      render: cells.text(s => (s?.tableName ?? '').trim() || '-'),
      // The card subtitle skips empty parts, so a sale without a table adds nothing there.
      value: s => tableLabelOf(s?.tableName),
      mobile: 'subtitle',
    },
    {
      key: 'user',
      label: 'User',
      width: 200,
      value: s => userNameOf(s),
      mobile: 'subtitle',
    },
    {
      key: 'location',
      label: 'Location',
      width: 200,
      value: s => s?.location?.name ?? '',
      mobile: 'subtitle',
    },
    {
      key: 'amount',
      label: 'Value',
      width: 130,
      align: 'right',
      render: cells.money(s => s?.amount, true),
      mobile: 'trailing',
    },
  ];

  // The API matches users / locations by name (ILIKE), so the option value is the name itself;
  // two records with the same name would be the same filter, so they collapse to one option.
  const nameOptions = (names: string[]): ListFilterOption[] =>
    Array.from(new Set(names.filter(name => name !== ''))).map(name => ({ value: name, label: name }));
  const filters: ListFilter[] = [
    {
      key: 'searchUser',
      label: 'User',
      kind: 'select',
      options: nameOptions(
        users.map(u => ((u?.user?.firstName ?? '') + ' ' + (u?.user?.lastName ?? '')).trim())
      ),
    },
    {
      key: 'searchLocation',
      label: 'Location',
      kind: 'select',
      options: nameOptions(locations.map(l => l?.name ?? '')),
    },
    { key: 'searchAmount', label: 'Amount', kind: 'text' },
    // One table: the API matches the whole table name, not part of it.
    { key: 'searchTable', label: 'Table number', kind: 'text' },
  ];

  const rowActions: ListRowAction<TransactionRow>[] = [
    {
      key: 'view',
      label: 'View',
      icon: <ViewIcon />,
      sheetIcon: 'chev',
      onRun: s => goToSale(String(s.id)),
    },
  ];

  return (
    <div
      className="h-full relative"
      style={{
        overflowY: isMobile ? 'auto' : undefined,
        paddingBottom: isMobile ? '24px' : undefined,
        display: isMobile ? undefined : 'flex',
        flexDirection: isMobile ? undefined : 'column',
      }}
    >
      <div className="lg:flex items-center justify-between">
        <p className="font-inter" style={{ fontSize: '24px', fontWeight: '500', color: '#222525' }}>
          {' '}
          {language && language?.['Recent sales report']
            ? language?.['Recent sales report']
            : 'Recent sales report'}
        </p>
        {
          isMobile &&
          <div className='space-y-2'
            style={{ marginTop: isMobile ? '20px' : '', padding: '12px', borderRadius: '12px', border: '1px solid #EBF0F0' }}
          >
            <p className="font-inter" style={{ fontSize: '14px', fontWeight: '400', color: '#4A5353' }}>
              {language && language?.['Total sales today']
                ? language?.['Total sales today']
                : 'Total sales today'}
            </p>
            <p className="font-inter" style={{ fontSize: '20px', fontWeight: '600', color: '#05292A' }}>
              {totalSales ?? '0'}
            </p>
          </div>
        }
      </div>
      <ListScreen
        ref={listRef}
        language={language}
        columns={columns}
        fetchRows={fetchTransactions}
        searchPlaceholder="Search sales no."
        searchInputRef={searchInputRef}
        filters={filters}
        defaultSort={{ sortBy: 'createdAt', sortDir: 'DESC' }}
        rowActions={rowActions}
        countNoun="sales"
        emptyState={inforTable}
      />
    </div>
  );
};
export default RecentSales;
